/**
 * "O que fazer agora" da lista de empresas: acoes derivadas dos numeros e da
 * prontidao de TODAS as empresas, na ordem em que custam dinheiro.
 *
 * Decisoes nao obvias:
 *  1. Ordem por gravidade: critico (a regua perde lead agora) > atencao (algo
 *     freia a recuperacao) > oportunidade > boa noticia. A equipe le de cima
 *     para baixo e para quando acabar o tempo.
 *  2. Toda acao tem um DESTINO concreto: recortar a lista (filtro) ou abrir a
 *     empresa ja na aba onde se resolve. Insight sem clique e so opiniao.
 *  3. Com uma empresa so, o texto diz o nome; com varias, diz quantas e cita
 *     ate tres. Centenas de empresas nao podem virar centenas de cartoes.
 *  4. Oportunidade tambem e DEPOIS do fato: "funil de expirados desligado com
 *     ofertas recorrentes" fala de assinaturas que JA venceram, nunca de
 *     lembrar antes do vencimento.
 */

import type { IconName } from '../../components/ui';
import { formatCount, formatPercentFromBps } from '../../lib/format';
import { descreverDias, diasDesde, variar } from './comparar';
import type { LinhaEmpresa } from './lista';
import { DIAS_INTEGRACAO_MUDA } from './saude';
import type { AbaDetalhe, ConsultaLista } from './types';
import { PROVEDOR_INFO } from './vocabulario';

export type AcaoInsight =
  | { readonly tipo: 'filtro'; readonly rotulo: string; readonly consulta: Partial<ConsultaLista> }
  | { readonly tipo: 'empresa'; readonly rotulo: string; readonly companyId: string; readonly aba: AbaDetalhe };

export type TomInsight = 'critical' | 'warning' | 'info' | 'good';

export interface InsightLista {
  readonly id: string;
  readonly tom: TomInsight;
  readonly icon: IconName;
  readonly titulo: string;
  readonly detalhe: string;
  readonly acao: AcaoInsight;
}

const ORDEM: Readonly<Record<TomInsight, number>> = { critical: 0, warning: 1, info: 2, good: 3 };
const DIAS_IMPLANTACAO_LONGA = 5;
const QUEDA_BPS = -2_000;

function nomes(linhas: readonly LinhaEmpresa[]): string {
  const lista = linhas.slice(0, 3).map((l) => l.company.name);
  const resto = linhas.length - lista.length;
  const base = lista.length <= 1 ? lista[0] ?? '' : `${lista.slice(0, -1).join(', ')} e ${lista[lista.length - 1] ?? ''}`;
  return resto > 0 ? `${base} e mais ${formatCount(resto)}` : base;
}

function abrir(linha: LinhaEmpresa, aba: AbaDetalhe, rotulo: string): AcaoInsight {
  return { tipo: 'empresa', rotulo, companyId: linha.company.id, aba };
}

export function gerarInsightsDaLista(linhas: readonly LinhaEmpresa[], agora: Date): readonly InsightLista[] {
  const insights: InsightLista[] = [];
  const naoPausadas = linhas.filter((l) => l.company.status !== 'pausada');
  const ativas = linhas.filter((l) => l.company.status === 'ativa');

  const semSegredo = naoPausadas.filter((l) => (l.item?.setup.providersMissingSecret.length ?? 0) > 0);
  const primeiraSemSegredo = semSegredo[0];
  if (primeiraSemSegredo !== undefined) {
    const provs = (primeiraSemSegredo.item?.setup.providersMissingSecret ?? []).map((p) => PROVEDOR_INFO[p].curto).join(' e ');
    insights.push({
      id: 'sem-segredo',
      tom: 'critical',
      icon: 'chave',
      titulo: semSegredo.length === 1 ? `${primeiraSemSegredo.company.name}: ${provs} sem segredo` : `${formatCount(semSegredo.length)} empresas recusando eventos com 401`,
      detalhe:
        semSegredo.length === 1
          ? 'Todo evento desse provedor volta 401: os checkouts abandonados não entram na régua. Cole o segredo e peça um evento de teste.'
          : `${nomes(semSegredo)} têm provedor conectado sem segredo. Nenhum abandono delas entra na régua.`,
      acao: abrir(primeiraSemSegredo, 'integracoes', semSegredo.length === 1 ? 'Abrir integrações' : `Começar por ${primeiraSemSegredo.company.name}`),
    });
  }

  const semWhatsapp = ativas.filter((l) => l.item?.setup.whatsapp === 'disconnected');
  const primeiraSemWhatsapp = semWhatsapp[0];
  if (primeiraSemWhatsapp !== undefined) {
    insights.push({
      id: 'whatsapp',
      tom: 'critical',
      icon: 'whatsapp',
      titulo: semWhatsapp.length === 1 ? `${primeiraSemWhatsapp.company.name} está sem WhatsApp` : `${formatCount(semWhatsapp.length)} empresas ativas sem WhatsApp`,
      detalhe:
        semWhatsapp.length === 1
          ? 'A sessão da Evolution caiu: os toques de WhatsApp da régua não saem até alguém ler o QR code de novo.'
          : `${nomes(semWhatsapp)} perderam a sessão da Evolution. Os toques de WhatsApp não saem.`,
      acao: abrir(primeiraSemWhatsapp, 'integracoes', 'Ver o WhatsApp'),
    });
  }

  const mudas = ativas.filter((l) => {
    const ultimo = l.item?.setup.lastWebhookAt ?? null;
    return ultimo !== null && diasDesde(ultimo, agora.toISOString()) >= DIAS_INTEGRACAO_MUDA;
  });
  const primeiraMuda = mudas[0];
  if (primeiraMuda !== undefined) {
    const dias = diasDesde(primeiraMuda.item?.setup.lastWebhookAt ?? agora.toISOString(), agora.toISOString());
    insights.push({
      id: 'integracao-muda',
      tom: 'warning',
      icon: 'raio',
      titulo: mudas.length === 1 ? `${primeiraMuda.company.name}: nenhum webhook há ${descreverDias(dias)}` : `${formatCount(mudas.length)} empresas sem webhook há dias`,
      detalhe: 'Sem checkout abandonado chegando, não há o que recuperar. Confira se a URL e o segredo continuam no painel do provedor.',
      acao: abrir(primeiraMuda, 'integracoes', 'Conferir integrações'),
    });
  }

  const implantacaoLonga = linhas.filter((l) => l.company.status === 'onboarding' && diasDesde(l.company.createdAt, agora.toISOString()) >= DIAS_IMPLANTACAO_LONGA);
  const primeiraLonga = implantacaoLonga[0];
  if (primeiraLonga !== undefined) {
    const passos = primeiraLonga.saude?.passos ?? [];
    const faltam = passos.filter((p) => !p.feito).length;
    const dias = diasDesde(primeiraLonga.company.createdAt, agora.toISOString());
    insights.push({
      id: 'implantacao',
      tom: 'warning',
      icon: 'config',
      titulo:
        implantacaoLonga.length === 1
          ? `${primeiraLonga.company.name} em implantação há ${descreverDias(dias)}`
          : `${formatCount(implantacaoLonga.length)} empresas em implantação há mais de ${DIAS_IMPLANTACAO_LONGA} dias`,
      detalhe:
        implantacaoLonga.length === 1
          ? `Faltam ${formatCount(faltam)} de ${formatCount(passos.length)} passos. Cada dia parado é abandono sem régua e comissão que não existe.`
          : `${nomes(implantacaoLonga)}. Cada dia parado é abandono sem régua.`,
      acao: implantacaoLonga.length === 1 ? abrir(primeiraLonga, 'resumo', 'Ver o que falta') : { tipo: 'filtro', rotulo: 'Ver empresas em implantação', consulta: { status: 'onboarding' } },
    });
  }

  const emQueda = ativas
    .map((l) => ({ l, v: l.item === null || l.item.previous === null ? null : variar(l.item.metrics.recoveredAmountCents, l.item.previous.recoveredAmountCents) }))
    .filter((x) => x.v !== null && x.v.relativaBps !== null && x.v.relativaBps <= QUEDA_BPS)
    .sort((a, b) => (a.v?.relativaBps ?? 0) - (b.v?.relativaBps ?? 0));
  const maiorQueda = emQueda[0];
  if (maiorQueda !== undefined && maiorQueda.v !== null && maiorQueda.v.relativaBps !== null) {
    insights.push({
      id: 'queda',
      tom: 'warning',
      icon: 'grafico',
      titulo:
        emQueda.length === 1
          ? `${maiorQueda.l.company.name}: recuperado caiu ${formatPercentFromBps(Math.abs(maiorQueda.v.relativaBps))}`
          : `${formatCount(emQueda.length)} empresas com recuperado em queda`,
      detalhe: 'Comparado com o período anterior de mesmo tamanho. Veja a saúde da conta: quase sempre é integração ou canal fora do ar.',
      acao: emQueda.length === 1 ? abrir(maiorQueda.l, 'resumo', 'Ver o resumo') : { tipo: 'filtro', rotulo: 'Ordenar pela maior queda', consulta: { ordem: { coluna: 'variacao', desc: false } } },
    });
  }

  const expiradosParados = naoPausadas.filter((l) => !l.company.funnels.includes('expirados') && (l.item?.setup.recurringOffers ?? 0) > 0);
  const primeiraParada = expiradosParados[0];
  if (primeiraParada !== undefined) {
    const recorrentes = primeiraParada.item?.setup.recurringOffers ?? 0;
    insights.push({
      id: 'expirados',
      tom: 'info',
      icon: 'calendario',
      titulo:
        expiradosParados.length === 1
          ? `${primeiraParada.company.name} pode ligar o funil de expirados`
          : `${formatCount(expiradosParados.length)} empresas com assinaturas e funil de expirados desligado`,
      detalhe:
        expiradosParados.length === 1
          ? `Tem ${formatCount(recorrentes)} ${recorrentes === 1 ? 'oferta recorrente' : 'ofertas recorrentes'}: assinaturas que já venceram ficam sem régua e sem comissão.`
          : `${nomes(expiradosParados)}. Assinaturas que já venceram ficam sem régua.`,
      acao: expiradosParados.length === 1 ? abrir(primeiraParada, 'resumo', 'Ver funis') : { tipo: 'filtro', rotulo: 'Ver quem só tem carrinho', consulta: { funil: 'carrinho_abandonado' } },
    });
  }

  const semVendedora = ativas.filter((l) => l.item !== null && l.item.setup.salesReps === 0);
  const primeiraSemVendedora = semVendedora[0];
  if (primeiraSemVendedora !== undefined) {
    insights.push({
      id: 'vendedora',
      tom: 'info',
      icon: 'usuarios',
      titulo: semVendedora.length === 1 ? `${primeiraSemVendedora.company.name} sem vendedora cadastrada` : `${formatCount(semVendedora.length)} empresas ativas sem vendedora`,
      detalhe:
        'Quem responde a um toque da régua cai no WhatsApp do comercial. Sem vendedora no painel, a venda fechada por ali não é registrada como venda assistida.',
      acao: abrir(primeiraSemVendedora, 'equipe', 'Convidar vendedora'),
    });
  }

  const aDefinir = naoPausadas.filter((l) => l.company.billingModel === 'a_definir');
  if (aDefinir.length > 0) {
    insights.push({
      id: 'cobranca',
      tom: 'info',
      icon: 'comissao',
      titulo: `${formatCount(aDefinir.length)} ${aDefinir.length === 1 ? 'empresa' : 'empresas'} com cobrança a definir`,
      detalhe: 'O modelo de cobrança da comissão (contrato x automático) ainda depende de decisão do dono. A comissão continua sendo apurada e fica visível para a empresa.',
      acao: { tipo: 'filtro', rotulo: 'Ver quais', consulta: { cobranca: 'a_definir' } },
    });
  }

  const emAlta = ativas
    .map((l) => ({ l, v: l.item === null || l.item.previous === null ? null : variar(l.item.metrics.recoveredAmountCents, l.item.previous.recoveredAmountCents) }))
    .filter((x) => x.v !== null && x.v.relativaBps !== null && x.v.relativaBps > 0)
    .sort((a, b) => (b.v?.relativaBps ?? 0) - (a.v?.relativaBps ?? 0));
  const maiorAlta = emAlta[0];
  if (maiorAlta !== undefined && maiorAlta.v !== null && maiorAlta.v.relativaBps !== null) {
    insights.push({
      id: 'alta',
      tom: 'good',
      icon: 'estrela',
      titulo: `${maiorAlta.l.company.name} recuperou ${formatPercentFromBps(maiorAlta.v.relativaBps)} a mais`,
      detalhe: 'Maior alta do período contra o anterior. Vale entender o que funcionou (oferta, horário, texto) e levar para as outras contas.',
      acao: abrir(maiorAlta.l, 'resumo', 'Ver o resumo'),
    });
  }

  // Rede de seguranca: a saude pode ser CRITICA por motivo sem regra propria
  // aqui (nenhum provedor conectado, nenhum webhook ate hoje). Sem isto, o
  // contador diria "1 critica" e este bloco, "nenhuma empresa perdendo lead".
  const criticas = linhas.filter((l) => l.saude?.nivel === 'critica');
  const primeiraCritica = criticas[0];
  if (primeiraCritica !== undefined && !insights.some((i) => i.tom === 'critical')) {
    insights.push({
      id: 'criticas',
      tom: 'critical',
      icon: 'alerta',
      titulo: criticas.length === 1 ? `${primeiraCritica.company.name} está em estado crítico` : `${formatCount(criticas.length)} empresas em estado crítico`,
      detalhe:
        criticas.length === 1
          ? (primeiraCritica.saude?.achados.find((x) => x.tom === 'critical')?.texto ?? 'A régua está perdendo leads agora.')
          : `${nomes(criticas)}. A régua delas está perdendo leads agora.`,
      acao: criticas.length === 1 ? abrir(primeiraCritica, primeiraCritica.saude?.achados.find((x) => x.tom === 'critical')?.aba ?? 'resumo', 'Ver o que falta') : { tipo: 'filtro', rotulo: 'Ver as críticas', consulta: { saude: 'critica' } },
    });
  }

  const emAtencao = linhas.filter((l) => l.saude?.nivel === 'atencao');
  const primeiraEmAtencao = emAtencao[0];
  if (primeiraEmAtencao !== undefined && !insights.some((i) => i.tom === 'warning')) {
    const achado = primeiraEmAtencao.saude?.achados.find((x) => x.tom === 'warning') ?? null;
    insights.push({
      id: 'em-atencao',
      tom: 'warning',
      icon: 'alerta',
      titulo: emAtencao.length === 1 ? `${primeiraEmAtencao.company.name} pede atenção` : `${formatCount(emAtencao.length)} empresas em atenção`,
      detalhe: emAtencao.length === 1 ? (achado?.texto ?? 'Algo está freando a recuperação.') : `${nomes(emAtencao)}. Algo está freando a recuperação delas.`,
      acao: emAtencao.length === 1 ? abrir(primeiraEmAtencao, achado?.aba ?? 'resumo', 'Ver o que falta') : { tipo: 'filtro', rotulo: 'Ver as em atenção', consulta: { saude: 'atencao' } },
    });
  }

  if (!insights.some((i) => i.tom === 'critical' || i.tom === 'warning') && linhas.length > 0) {
    insights.push({
      id: 'tudo-em-dia',
      tom: 'good',
      icon: 'check',
      titulo: 'Nenhuma empresa perdendo lead agora',
      detalhe: 'Integrações com segredo, WhatsApp conectado e webhooks chegando em todas as contas ativas.',
      acao: { tipo: 'filtro', rotulo: 'Ver as saudáveis', consulta: { saude: 'saudavel' } },
    });
  }

  return insights.sort((a, b) => ORDEM[a.tom] - ORDEM[b.tom]);
}
